import {
    Component,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    Optional,
    output,
    OutputEmitterRef,
    SkipSelf,
} from '@angular/core';
import { UIRouter } from '@uirouter/angular';
import { AceLoaderComponent } from '../../UI-Lib/ACE/Loader/loader.component';
import { HelpTooltipDirective } from '../../UI-Lib/Help-Tooltip';
import { BundlerFiltersComponent } from './Bundler-Filters/bundlerFilters.component';
import { FeatureFlagsService } from '../../Common/FeatureFlags/feature-flags-service';
import { BundlerOrganizeListComponent } from './Bundler-Oganize-List/bundlerOrganizeList.component';
import { AttachmentBundlerService } from './attachmentBundler.service';
import { AttachmentBundlerRepository } from './attachmentBundler.repository';
import { ReportType } from 'src/app/Reporting/Manager/report-type';
import { BundlerSelectionListComponent } from './Bundler-Selection-List/bundlerSelectionList.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AttachmentBundlerContext } from '../attachment.modal.model';

interface BundlerStep {
    title: string;
    buttonText?: string;
}

@Component({
    selector: 'attachment-bundler',
    templateUrl: './attachmentBundler.component.html',
    styleUrls: ['./attachmentBundler.component.scss'],
    imports: [
        AceLoaderComponent,
        HelpTooltipDirective,
        BundlerFiltersComponent,
        BundlerOrganizeListComponent,
        BundlerSelectionListComponent,
    ],
    providers: [
        {
            provide: AttachmentBundlerService,
            useFactory: (existing: AttachmentBundlerService | null, repo: AttachmentBundlerRepository) => {
                return existing ?? new AttachmentBundlerService(repo);
            },
            deps: [[new Optional(), new SkipSelf(), AttachmentBundlerService], AttachmentBundlerRepository],
        },
    ],
})
export class AttachmentBundlerComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _router: UIRouter,
        private readonly _featureFlagsService: FeatureFlagsService,
        private readonly _attachmentBundlerService: AttachmentBundlerService,
    ) {}

    bundlerTarget: InputSignal<Core.AttachmentBundlerScopeEnum> = input.required();
    invoiceContext = input<AttachmentBundlerContext | undefined>(undefined);
    dialogSubmit: OutputEmitterRef<string[]> = output<string[]>();
    dialogCancel: OutputEmitterRef<void> = output<void>();

    bundlerSteps: Map<Core.AttachmentBundlerScopeEnum, BundlerStep> = new Map([
        [Core.AttachmentBundlerScopeEnum.Invoice, { title: 'Attach to Invoice' }],
        [
            Core.AttachmentBundlerScopeEnum.LitigationReferral,
            {
                title: 'Attach to Litigation Referral',
            },
        ],
        [Core.AttachmentBundlerScopeEnum.Company, { title: 'Select Attachments', buttonText: 'Next' }],
        [Core.AttachmentBundlerScopeEnum.Jurisdiction, { title: 'Select Attachments', buttonText: 'Next' }],
    ]);

    processStep: BundlerStep = { title: 'Download' };
    currentStep: BundlerStep;
    reportType: ReportType = ReportType.AttachmentBundlerCompany;

    isDialogMode: boolean;
    loading = true;
    nextButtonEnabled: boolean = false;
    private _selectedAttachmentIds: string[] = [];

    private _destroy$: Subject<void> = new Subject();

    ngOnInit(): void {
        this.isDialogMode =
            this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Invoice ||
            this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.LitigationReferral;
        if (!this._featureFlagsService.featureFlags.enableAttachmentBundler) {
            if (this.isDialogMode) {
                this.dialogCancel.emit();
                return;
            } else {
                this._router.stateService.go('home');
                return;
            }
        }

        this.currentStep = this.bundlerSteps.get(this.bundlerTarget());

        this.reportType =
            this.bundlerTarget() === Core.AttachmentBundlerScopeEnum.Jurisdiction
                ? ReportType.AttachmentBundlerJurisdiction
                : ReportType.AttachmentBundlerCompany;
        this._attachmentBundlerService.setSearchScope(this.bundlerTarget());

        // Configure already-attached IDs from invoice context
        const ctx = this.invoiceContext();
        if (ctx) {
            this._attachmentBundlerService.setDialogContext(ctx);

            if (ctx.alreadyAttachedIds?.length) {
                this._attachmentBundlerService.setAlreadyAttachedIds(ctx.alreadyAttachedIds);
            }
        }

        this._attachmentBundlerService.selectedAttachments$.pipe(takeUntil(this._destroy$)).subscribe((data) => {
            this.nextButtonEnabled = data.length > 0;
            this._selectedAttachmentIds = data.map((a) => a.attachmentId as string);
        });

        this._attachmentBundlerService.loading$
            .pipe(takeUntil(this._destroy$))
            .subscribe((loading) => (this.loading = loading));

        this.loading = false;
    }

    ngOnDestroy(): void {
        this._attachmentBundlerService.cancelActiveLoad();
        this._destroy$.next();
        this._destroy$.complete();
    }

    get isStickyHeaderEnabled(): boolean {
        return !this.isDialogMode && !!this.currentStep && this.currentStep !== this.processStep;
    }

    async nextStep(): Promise<void> {
        if (this.isDialogMode) {
            this._submitDialogResult();
            return;
        }

        if (this.currentStep !== this.processStep) {
            this.currentStep = this.processStep;
            this.nextButtonEnabled = this._attachmentBundlerService.outputFormValid;
        } else {
            await this._attachmentBundlerService.bundle();
        }
    }

    previousStep(): void {
        if (this.currentStep === this.processStep) {
            this.currentStep = this.bundlerSteps.get(this.bundlerTarget());
            this.nextButtonEnabled = !!this._selectedAttachmentIds.length;
        }
    }

    cancelDialog(): void {
        this.dialogCancel.emit();
    }

    private _submitDialogResult(): void {
        this.dialogSubmit.emit(this._selectedAttachmentIds);
    }
}
